import { Popover } from "@mui/material";
import { useState } from "react";
import { BiEditAlt } from "react-icons/bi";
import { HiOutlineTrash } from "react-icons/hi";
import { MdOutlinePersonRemove } from "react-icons/md";
import { PiUserSwitch } from "react-icons/pi";
import { EmployeeDto } from "../../../../models/employee.dto";
import { useUnlinkEmployeeFromCompanyTree } from "../../../../services/company-tree-node.service";
import { useOrganizationTreeStore, TreeNode } from "../../../../state-management/useOrganizationTreeStore";
import ReassignEmployeeModal from "./ReassignEmployeeModal";
import UpdateParentNodeModal from "./UpdateParentNodeModal";
import DeleteNodeModal from "./DeleteNodeModal";
import API from '../../../../services/utils/api';
import { TbUserEdit } from "react-icons/tb";
import { JobTitleDto } from "../../../../models/job-titles.dto";
import UpdateJobTitleModal from "./UpdateJobTitleModal";
import useCompanyStore from "../../../../state-management/useCompanyStore";

type NodeMenuProps = {
    anchorEl: any;
    node: TreeNode;
    jobTitles: JobTitleDto[];
    employees: EmployeeDto[];
    handleClose: (closeMenu?: any) => void;
}

const NodeMenu = ({
    anchorEl,
    node,
    jobTitles,
    employees,
    handleClose,
}: NodeMenuProps) => {
	const { currentCompany } = useCompanyStore();
    const { treeSourceData, generateTree } = useOrganizationTreeStore();
	const [disableNodeMenu, setDisableNodeMenu] = useState(false);
	const [reassignEmployeePopoverEl, setReassignEmployeePopoverEl] = useState<any>(null);
	const [updateJobTitlePopoverEl, setUpdateJobTitlePopoverEl] = useState<any>(null);
	const [updateParentNodePopoverEl, setUpdateParentNodePopoverEl] = useState<any>(null);
	const [deleteNodePopoverEl, setDeleteNodePopoverEl] = useState<any>(null);

	const closeReassignEmployeePopover = (closeMenu?: any) => {
		setReassignEmployeePopoverEl(null);
		closeMenu === "true" && handleClose();
	};
	const closeUpdateJobTitlePopover = (closeMenu?: any) => {
		setUpdateJobTitlePopoverEl(null);
		closeMenu === "true" && handleClose();
	};
	const closeUpdateParentNodePopover = (closeMenu?: any) => {
		setUpdateParentNodePopoverEl(null);
		closeMenu === "true" && handleClose();
	};
	const closeDeleteNodePopover = (closeMenu?: any) => {
		setDeleteNodePopoverEl(null);
		closeMenu === "true" && handleClose();
	};
	
    const { mutate: unLinkEmployee } = useUnlinkEmployeeFromCompanyTree();

	const handleUnLinkEmployee = () => {
		setDisableNodeMenu(true);

		unLinkEmployee(
			{ 
				companyId: currentCompany?.id as number,
				nodeId: node?.id as number,
			},
			{
				onSuccess: async () => {
                    const { data } = await API.HrAPI.getCompanyTree(currentCompany?.id as number);
                    generateTree(data);
					handleClose();
					setDisableNodeMenu(false);
				},
				onError: () => setDisableNodeMenu(false),
			}
		)
	};

    return (<>
        <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={() => handleClose()}
            anchorOrigin={{
                vertical: "bottom",
                horizontal: "left",
            }}
            transformOrigin={{
                vertical: "top",
                horizontal: "left",
            }}
            PaperProps={{
                style: {
                    height: "auto",
                    fontSize: "15px",
                    color: "#4F4F4F",
                    borderRadius: 10,
                    background: "white",
                    boxShadow: "-10px -5px 30px 0px rgba(0, 0, 0, 0.15)",
                    overflow: "hidden",
                }
            }}
        >
            <button 
                onClick={(e) => setReassignEmployeePopoverEl(e.currentTarget)}
                disabled={disableNodeMenu}
                className={`${reassignEmployeePopoverEl ? "bg-[#F4F4F4]" : "hover:bg-[#F4F4F4]"} 
                    w-full pl-4 pr-6 py-3 flex items-center gap-2 node-button 
                `}
            >
                <PiUserSwitch className="text-[18px]" />
                <span>Re-assign</span>
            </button>
            <button 
                onClick={(e) => setUpdateJobTitlePopoverEl(e.currentTarget)}
                disabled={disableNodeMenu}
                className={`${updateJobTitlePopoverEl ? "bg-[#F4F4F4]" : "hover:bg-[#F4F4F4]"} 
                    w-full pl-4 pr-6 py-3 flex items-center gap-2 node-button 
                `}
            >
                <BiEditAlt className="text-[18px]" />
                <span>Update Job Title</span>
            </button>
            <button 
                onClick={(e) => setUpdateParentNodePopoverEl(e.currentTarget)}
                disabled={disableNodeMenu}
                className={`${updateParentNodePopoverEl ? "bg-[#F4F4F4]" : "hover:bg-[#F4F4F4]"} 
                    w-full pl-4 pr-6 py-3 flex items-center gap-2 node-button 
                `}
            >
                <TbUserEdit className="text-[18px]" />
                <span>Change Supervisor</span>
            </button>
            <button 
                onClick={handleUnLinkEmployee}
                disabled={disableNodeMenu}
                className="w-full pl-4 pr-6 py-3 flex items-center gap-2 node-button hover:bg-[#F4F4F4]"
            >
                <MdOutlinePersonRemove className="text-[18px]" />
                <span>Remove Employee</span>
            </button>
            <button 
                onClick={(e) => setDeleteNodePopoverEl(e.currentTarget)}
                disabled={disableNodeMenu}
                className={`${deleteNodePopoverEl ? "bg-[#F4F4F4]" : "hover:bg-[#F4F4F4]"} 
                    w-full pl-4 pr-6 py-3 flex items-center gap-2 node-button 
                `}
            >
                <HiOutlineTrash className="text-[18px]" />
                <span>Delete</span>
            </button>
        </Popover>

        {reassignEmployeePopoverEl && (
            <ReassignEmployeeModal
                anchorEl={reassignEmployeePopoverEl}
                selectedNode={node}
                employees={
                    employees?.filter((employee) => {
                        return !treeSourceData.find(node => node.data.employeeId === employee.id)
                    })
                }
                handleClose={closeReassignEmployeePopover}
            />
        )}

        {updateJobTitlePopoverEl && (
            <UpdateJobTitleModal
                anchorEl={updateJobTitlePopoverEl}
                selectedNode={node}
                jobTitles={jobTitles}
                handleClose={closeUpdateJobTitlePopover}
            />
        )}

        {updateParentNodePopoverEl && (
            <UpdateParentNodeModal
                anchorEl={updateParentNodePopoverEl}
                node={node}
                validParentNodes={
                    treeSourceData
                    .filter(treeNode => {
                        if (
                            treeNode.id !== node.id && 
                            treeNode.id !== node.parentId &&
                            treeNode.data.employee
                        ) {
                            return treeNode
                        }
                    })
                    .sort((a, b) => {
                        const fullNameA = a.data.employee?.firstName + ' ' + a.data.employee?.lastName;
                        const fullNameB = b.data.employee?.firstName + ' ' + b.data.employee?.lastName;
                        return fullNameA.localeCompare(fullNameB);
                    })
                }
                handleClose={closeUpdateParentNodePopover}
            />
        )}

        {deleteNodePopoverEl && (
            <DeleteNodeModal
                anchorEl={deleteNodePopoverEl}
                nodeId={node.id}
                childNodes={node.children || []}
                validParentNodes={
                    treeSourceData
                    .filter(treeNode => {
                        if (
                            treeNode.id !== node.id && 
                            treeNode.parentId !== node.id &&
                            treeNode.data.employee
                        ) {
                            return treeNode
                        }
                    })
                    .sort((a, b) => {
                        const fullNameA = a.data.employee?.firstName + ' ' + a.data.employee?.lastName;
                        const fullNameB = b.data.employee?.firstName + ' ' + b.data.employee?.lastName;
                        return fullNameA.localeCompare(fullNameB);
                    })
                }
                handleClose={closeDeleteNodePopover}
            />
        )}
    </>);
}
 
export default NodeMenu;